import {useCallback} from 'react'
import {ChatMessage} from 'yougile-model'
import {ClientUtilities} from '@client/client-utilities'
import {Tracking, TrackingEvent} from '@common/tracking'
import {ChatMessageBaseProps} from '@components/common/base/chat-message'
import {useFileClick} from '@components/right-panel/chat/chat-messages/message/utils/use-file-click'
import {globals} from '@extensions/globals-base'
import {Task} from '@extensions/task'
import {useChatLabelsStore} from '../../../chat-messages'
import {useChatStore} from '../../../chat-store'
import {useMessageClick} from './use-message-click'

export type UseMessageActions = Pick<
  ChatMessageBaseProps,
  | 'onMessageClick'
  | 'onLabelSubmit'
  | 'onReply'
  | 'onForward'
  | 'onTogglePin'
  | 'onEditPin'
  | 'onEdit'
  | 'onDelete'
  | 'onFileClick'
  | 'onSetBackground'
  | 'onRemoveBackground'
  | 'onDownloadFile'
  | 'onMarkUnread'
  | 'onMarkImportant'
>

export const useMessageActions = (msg: ChatMessage): UseMessageActions => {
  const [state, store] = useChatStore()
  const [, labelsStore] = useChatLabelsStore()

  const canSendMessage = store.state.canSendMessages
  const canPinMessage =
    (!store.isComments || store.isExtensionCommentsEditor) &&
    (!!msg.label || labelsStore.state.allowEditPins)
  const canEditMessage =
    store.state.canSendMessages && msg.from === globals.currentUserId

  const canDeleteMessage = store.isExtensionCommentsEditor

  const backgroundAvailable = !state.noTabs && !state.groupChat

  // HACK: subscribing to state.messageToEdit causes rerender of edited messages
  const editMessage = useCallback(
    () => store.startEdit(msg),
    [msg, store, state.messageToEdit]
  )
  const deleteMessage = useCallback(() => {
    void store.deleteMessage(msg)
  }, [msg, store])
  const forwardMessage = useCallback(
    () => store.setMessageToForward(msg),
    [msg, store]
  )
  const replyMessage = useCallback(
    () => store.setMessageToReply(msg),
    [msg, store]
  )
  const startLabelEdit = useCallback(() => {
    if (store.isExtensionComments) {
      if (!store.isExtensionCommentsEditor) {
        return
      }
    }
    labelsStore.startEdit(msg)
  }, [labelsStore, msg])
  const removeLabel = useCallback(
    () => labelsStore.removeLabel(msg),
    [labelsStore, msg]
  )
  const onTogglePin = msg.label ? removeLabel : startLabelEdit
  const labelSubmit = useCallback(
    (label: string) => labelsStore.stopEditNew(label),
    [labelsStore]
  )
  const messageClick = useMessageClick()
  const onFileClick = useFileClick()
  const onSetBackground = useCallback(
    (url: string) => {
      const hub = store.state.hub
      ClientUtilities.createPreviewForServerImage(
        url,
        {height: 160, width: 800},
        url => {
          if (hub && url) {
            hub.data.image = url
            hub.commit()
            void Tracking.send(TrackingEvent.taskCoverUploadCoverViaChat)
          }
        }
      )
    },
    [store]
  )
  const onRemoveBackground = useCallback(() => {
    const hub = store.state.hub
    hub && Task.removeTaskCover(hub)
  }, [store])
  const onDownloadFile = useCallback((url: string) => {
    ClientUtilities.downloadFile(url)
    void Tracking.send(TrackingEvent.taskCoverDownloadViaChat)
  }, [])
  const onMarkUnread = useCallback(
    () => store.markUnread(msg.timestamp),
    [store, msg.timestamp]
  )
  const onMarkImportant = useCallback(
    () => store.markImportant(msg.timestamp),
    [store, msg.timestamp]
  )

  return {
    onMessageClick: messageClick,
    onLabelSubmit: labelSubmit,
    onReply: canSendMessage ? replyMessage : undefined,
    onForward: store.canForwardMessage ? forwardMessage : undefined,
    onTogglePin: canPinMessage ? onTogglePin : undefined,
    onEditPin: startLabelEdit,
    onEdit: canEditMessage ? editMessage : undefined,
    onDelete: canDeleteMessage ? deleteMessage : undefined,
    onFileClick,
    onSetBackground: backgroundAvailable ? onSetBackground : undefined,
    onRemoveBackground,
    onDownloadFile,
    onMarkUnread,
    onMarkImportant
  }
}
